import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ShowChartOutlined from "@mui/icons-material/ShowChartOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_CHART_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import {
  GetRevenueCostProfitResponse,
  RevenueCostProfitPeriod,
} from "subModule/src/services/analytics/interface";
import { useGetRevenueCostProfitService } from "subModule/src/services/analytics/useAnalytics";
import { TFunction } from "i18next";
import { PieChart } from "@mui/x-charts/PieChart";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import ComparisonChange from "./ComparisonChange";

interface RevenueCostProfitProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

/** Map UI range filter → preferred API period object keys (in priority order). */
const RANGE_PERIOD_KEYS: Record<string, string[]> = {
  daily: ["daily", "today", "currentDay"],
  yesterday: ["yesterday", "lastDay", "previousDay"],
  weekly: ["weekly", "currentWeek", "week"],
  monthly: ["monthly", "currentMonth", "month"],
  last_month: ["lastMonth", "last_month"],
  yearly: ["yearly", "currentYear", "year"],
};

const COMPARISON_OR_META_KEYS = new Set([
  "comparison",
  "previousWeek",
  "previousMonth",
  "previousDay",
  "previousYear",
  "previous",
]);

function isPeriodObject(value: unknown): value is RevenueCostProfitPeriod {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  return (
    "Revenue" in obj ||
    "Cost" in obj ||
    "Profit" in obj ||
    "revenue" in obj ||
    "cost" in obj ||
    "profit" in obj
  );
}

function readPeriodNumbers(period: Record<string, unknown> | null | undefined): RevenueCostProfitPeriod {
  if (!period || typeof period !== "object") {
    return { Revenue: 0, Cost: 0, Profit: 0 };
  }
  const toNum = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    Revenue: toNum(period.Revenue ?? period.revenue),
    Cost: toNum(period.Cost ?? period.cost),
    Profit: toNum(period.Profit ?? period.profit),
  };
}

function resolvePeriodData(
  raw: any,
  range?: string,
): RevenueCostProfitPeriod & {
  revenuePercentage?: number;
  costPercentage?: number;
  profitPercentage?: number;
} {
  if (!raw || typeof raw !== "object") {
    return { Revenue: 0, Cost: 0, Profit: 0 };
  }

  const rangeKey = range || "monthly";
  const preferredKeys = RANGE_PERIOD_KEYS[rangeKey] || [rangeKey];

  let nested: Record<string, unknown> | null = null;

  for (const key of preferredKeys) {
    if (isPeriodObject(raw[key])) {
      nested = raw[key] as Record<string, unknown>;
      break;
    }
  }

  // Any nested period object that isn't comparison / previous* baseline
  if (!nested) {
    for (const [key, value] of Object.entries(raw)) {
      if (COMPARISON_OR_META_KEYS.has(key)) continue;
      if (isPeriodObject(value)) {
        nested = value as Record<string, unknown>;
        break;
      }
    }
  }

  // Last resort: previous* period (better than blank zeros if that's all we got)
  if (!nested) {
    for (const [key, value] of Object.entries(raw)) {
      if (key === "comparison") continue;
      if (isPeriodObject(value)) {
        nested = value as Record<string, unknown>;
        break;
      }
    }
  }

  const period = nested
    ? readPeriodNumbers(nested)
    : readPeriodNumbers(raw as Record<string, unknown>);

  const comparison = raw?.comparison || {};

  return {
    ...period,
    revenuePercentage: comparison?.revenuePercentage,
    costPercentage: comparison?.costPercentage,
    profitPercentage: comparison?.profitPercentage,
  };
}

export default function RevenueCostProfit(props: RevenueCostProfitProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<any>(null);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px)",
  );
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetRevenueCostProfitService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    const rangeValue = watch("range")?.value;
    await apiRefetch()
      .then((res: GetRevenueCostProfitResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(resolvePeriodData(res?.data?.data || {}, rangeValue));
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const metricRows = [
    {
      key: "revenue",
      label: t("revenue"),
      percentage: data?.revenuePercentage,
      amount: data?.Revenue,
      color: props.themes.primary,
    },
    {
      key: "cost",
      label: t("directCost"),
      percentage: data?.costPercentage,
      amount: data?.Cost,
      color: props.themes.secondary,
    },
    {
      key: "profit",
      label: data?.Profit && data?.Profit < 0 ? t("loss") : t("grossProfit"),
      percentage: data?.profitPercentage,
      amount: data?.Profit,
      color: "#1CACDC",
    },
  ] as const;

  return (
    <Card
      sx={{
        height: DASHBOARD_CHART_WIDGET_HEIGHT,
        minHeight: DASHBOARD_CHART_WIDGET_HEIGHT,
        marginTop: "16px",
        boxShadow: "none",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "12px 16px 14px !important",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
          "&:last-child": { paddingBottom: "14px !important" },
        },
      }}
    >
      <CardContent>
        <Box sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0 }}>
          <AnalyticsWidgetHeader
            title={t("revenueCostProfit")}
            icon={<ShowChartOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: "9px",
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "revenue"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "revenue",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "revenue",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions("revenue", "loadNext");
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: "9px",
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Skeleton
              variant="circular"
              width={130}
              height={130}
              sx={{
                backgroundColor: props?.themes?.secondary,
                margin: "8px auto",
              }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <DemoPie data={data} themes={props.themes} {...{ t }} />
            </Box>

            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1, sm: 1.25 },
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              {metricRows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(72px, 0.9fr) minmax(0, 1.1fr) minmax(0, 1.4fr)",
                    alignItems: "center",
                    columnGap: { xs: 1, sm: 1.5 },
                    rowGap: 0.5,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    className="font-Saira text-start"
                    sx={{
                      fontSize: { xs: 13, sm: 14 },
                      fontWeight: 500,
                      color: "#374151",
                      minWidth: 0,
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Tooltip
                    placement="top"
                    title={row.amount?.toLocaleString() || 0}
                    enterDelay={0}
                  >
                    <Box
                      className="flex gap-1 items-baseline"
                      sx={{
                        minWidth: 0,
                        justifyContent: "flex-start",
                      }}
                    >
                      <span
                        className="font-Saira font-bold"
                        style={{
                          color: row.color,
                          fontSize: 14,
                          lineHeight: 1.2,
                        }}
                      >
                        {new Intl.NumberFormat("en", {
                          notation: "compact",
                          compactDisplay: "short",
                          maximumFractionDigits: 2,
                        }).format(Math.abs(row.amount || 0))}
                      </span>
                      <span className="font-Saira uppercase text-[10px] font-medium">
                        {props?.user?.business?.currency || ""}
                      </span>
                    </Box>
                  </Tooltip>
                  <Box
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <ComparisonChange
                      percentage={row.percentage}
                      range={values[1]?.value}
                      dense
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

const DemoPie = ({
  data,
  themes,
  t,
}: {
  data: any;
  themes?: any;
  t: TFunction<"translation", undefined>;
}) => {
  const isNarrow = useMediaQuery("(max-width: 600px)");
  const chartHeight = isNarrow ? 140 : 155;
  const outerRadius = isNarrow ? 58 : 68;
  const innerRadius = isNarrow ? 34 : 40;
  const revenue = Number(data?.Revenue) || 0;
  const cost = Number(data?.Cost) || 0;
  const profit = Number(data?.Profit) || 0;
  const allZero = Math.abs(revenue) + Math.abs(cost) + Math.abs(profit) === 0;
  const mappedData = [
    {
      label: profit < 0 ? t("loss") : t("grossProfit"),
      value: allZero ? 1 : Math.abs(profit),
      color: "#1CACDC",
    },
    {
      label: t("directCost"),
      value: allZero ? 1 : Math.abs(cost),
      color: themes.secondary,
    },
    {
      label: t("revenue"),
      value: allZero ? 1 : Math.abs(revenue),
      color: themes.primary,
    },
  ];
  return (
    <Box
      sx={{
        mx: "auto",
        width: "100%",
        maxWidth: 200,
        display: "flex",
        justifyContent: "center",
        "& .MuiChartsLegend-root": { display: "none" },
      }}
    >
      <PieChart
        height={chartHeight}
        width={isNarrow ? 200 : 220}
        series={[
          {
            data: mappedData,
            valueFormatter: (v) => {
              return `${allZero ? 0 : v.value?.toLocaleString() || 0}`;
            },
            innerRadius,
            outerRadius,
          },
        ]}
        skipAnimation={false}
        slotProps={{
          legend: { hidden: true },
        }}
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
      />
    </Box>
  );
};
